// The Relevance Framework - Detailed Content
// A structured communication and decision-making model

const relevanceFrameworkData = {
  encodedId: 'gf.relevance.framework',
  
  definition: {
    title: 'The Relevance Framework',
    shortDescription: 'A structured communication and decision-making model that ensures ideas, initiatives, and priorities are articulated with clarity, meaning, and practical grounding.',
    fullDescription: 'The Relevance Framework operates through three sequential questions that create coherence between concept, purpose, and practice, ensuring that every idea holds relevance across personal, team, and organizational levels.'
  },

  coreQuestions: [
    {
      question: 'What is it?',
      purpose: 'Define the essence clearly',
      focus: 'Clarity',
      description: 'Establishes precise boundaries and removes ambiguity by defining terms and core concepts.',
      outcome: 'Clear understanding of the concept, entity, or initiative',
      antiPattern: 'What-only: Shallow communication that defines without providing significance or application'
    },
    {
      question: 'Why is it important?',
      purpose: 'Connect it to significance, stakes, and value',
      focus: 'Significance',
      description: 'Anchors focus on what truly matters, connecting the concept to meaningful stakes and avoiding distraction by noise.',
      outcome: 'Understanding of impact, value, and consequences',
      antiPattern: 'Why-only: Ungrounded inspiration that lacks clear definition or practical mechanics'
    },
    {
      question: 'How does it work?',
      purpose: 'Demonstrate its mechanics and application',
      focus: 'Application',
      description: 'Bridges theory into lived practice, enabling teams to act congruently with clear understanding.',
      outcome: 'Practical knowledge of implementation and operation',
      antiPattern: 'How-only: Mechanical instruction without meaning or context'
    }
  ],

  purpose: {
    clarity: 'Removes ambiguity by defining terms and boundaries',
    significance: 'Anchors focus on what truly matters, avoiding distraction by noise',
    application: 'Bridges theory into lived practice, enabling teams to act congruently'
  },

  principles: [
    'Relevance emerges when all three dimensions (What, Why, How) are aligned',
    'Sequential progression: Each question builds on the previous one',
    'Universal application: Works at personal, team, and organizational levels',
    'Coherence creation: Aligns concept, purpose, and practice into a unified whole',
    'Anti-fragile communication: Prevents common pitfalls in leadership discourse'
  ],

  antiPatterns: [
    {
      name: 'Shallow Communication (What-only)',
      description: 'Provides definition without connecting to significance or demonstrating application',
      consequence: 'Teams understand terms but not why they matter or how to apply them',
      example: 'Defining OKRs without explaining their strategic value or implementation process'
    },
    {
      name: 'Ungrounded Inspiration (Why-only)',
      description: 'Offers compelling vision and motivation without clear definition or practical steps',
      consequence: 'Teams feel inspired but lack clarity on what to do or how to proceed',
      example: 'Rallying around "customer obsession" without defining it or providing concrete practices'
    },
    {
      name: 'Mechanical Instruction (How-only)',
      description: 'Gives step-by-step instructions without explaining what is being built or why it matters',
      consequence: 'Teams execute without understanding, leading to rigid compliance over intelligent adaptation',
      example: 'Following agile ceremonies without understanding agile principles or their strategic purpose'
    }
  ],

  applicationLevels: [
    {
      level: 'Individual / Personal',
      timeframe: '5-15 minutes',
      context: 'Personal clarity, learning new concepts, making individual decisions',
      example: 'Understanding a new prioritization technique before applying it to your work',
      depth: 'Sufficient for personal comprehension and action'
    },
    {
      level: 'Team',
      timeframe: '30-60 minutes',
      context: 'Team alignment, shared understanding, collaborative decision-making',
      example: 'Aligning a team on a new process before rolling it out',
      depth: 'Ensures all team members share common understanding across all three dimensions'
    },
    {
      level: 'Organization',
      timeframe: 'Multiple sessions',
      context: 'Strategic initiatives, organizational change, cross-functional alignment',
      example: 'Launching a company-wide transformation initiative',
      depth: 'Comprehensive articulation with stakeholder-specific variations'
    }
  ],

  useCases: [
    'Introducing new frameworks or methodologies',
    'Aligning teams on strategic priorities',
    'Coaching leaders to communicate more effectively',
    'Evaluating proposed initiatives for completeness',
    'Clarifying decision-making criteria',
    'Designing training and development programs',
    'Facilitating cross-functional collaboration',
    'Creating organizational documentation'
  ],

  integrationWithOtherFrameworks: [
    {
      framework: 'DEGAP (Five Stages)',
      connection: 'Relevance Framework can be applied at each DEGAP stage to ensure clarity, significance, and application',
      example: 'In the Decide stage: What are we deciding? Why does this decision matter? How will we make it?'
    },
    {
      framework: 'Prioritization Capabilities (12C/4F/2O)',
      connection: 'Each capability can be articulated through the Relevance Framework for deeper understanding',
      example: 'Stakeholder Intelligence: What is it? Why does it matter for prioritization? How do we develop it?'
    },
    {
      framework: 'Four Modes of Work',
      connection: 'Relevance Framework clarifies the boundaries and transitions between modes',
      example: 'Strategy vs. Prioritization: What distinguishes them? Why does the distinction matter? How do they interact?'
    }
  ],

  assessmentQuestions: [
    'Have we clearly defined what this is (essence)?',
    'Have we articulated why this matters (significance)?',
    'Have we demonstrated how this works (mechanics)?',
    'Are all three dimensions coherent and aligned?',
    'Can stakeholders articulate all three dimensions independently?',
    'Are we falling into any anti-patterns (what-only, why-only, how-only)?'
  ]
};

export default relevanceFrameworkData;
